import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { Brand } from "@championcare/ui";
import { COGNITO_ENABLED } from "../config";

// Password reset. With Cognito configured this is the real flow: resetPassword emails a code,
// confirmResetPassword sets the new password. On dev-sbx1 (no Cognito) it stays a simulated flow
// that lands on the check-email confirmation.
function CognitoResetPassword() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [step, setStep] = useState<"request" | "confirm" | "done">("request");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submitRequest(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { nextStep } = await resetPassword({ username: email.trim() });
      if (nextStep.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") setStep("confirm");
      else setStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn’t start the reset. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function submitConfirm(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await confirmResetPassword({
        username: email.trim(),
        confirmationCode: code.trim(),
        newPassword,
      });
      setStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "That didn’t work — check the code and try again.");
    } finally {
      setBusy(false);
    }
  }

  if (step === "done") {
    return (
      <div className="pt-auth">
        <div className="pt-auth__card" style={{ textAlign: "center" }}>
          <div className="pt-auth__brand">
            <Brand size={28} />
          </div>
          <h1 className="pt-auth__title">
            Password <em>updated.</em>
          </h1>
          <p className="pt-auth__lede">Your password has been reset. Sign in with your new password.</p>
          <Link to="/sign-in" className="pt-btn pt-btn--primary pt-auth__submit" style={{ display: "inline-flex" }}>
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  if (step === "confirm") {
    return (
      <div className="pt-auth">
        <div className="pt-auth__card">
          <div className="pt-auth__brand">
            <Brand size={28} />
          </div>
          <h1 className="pt-auth__title">
            Check your <em>email.</em>
          </h1>
          <p className="pt-auth__lede">
            We sent a code to <strong>{email}</strong>. Enter it below with your new password.
          </p>
          <form onSubmit={submitConfirm}>
            <div className="pt-field">
              <label htmlFor="code">Reset code</label>
              <input
                id="code"
                className="pt-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                required
              />
            </div>
            <div className="pt-field">
              <label htmlFor="new-password">New password</label>
              <input
                id="new-password"
                className="pt-input"
                type="password"
                autoComplete="new-password"
                placeholder="12+ characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={12}
              />
            </div>
            {error && (
              <p className="pt-auth__note" role="alert" style={{ color: "var(--red)" }}>{error}</p>
            )}
            <button type="submit" className="pt-btn pt-btn--primary pt-auth__submit" disabled={busy || code.length < 6}>
              {busy ? "Resetting…" : "Reset password →"}
            </button>
          </form>
          <p className="pt-auth__alt">
            <Link to="/sign-in" className="pt-auth__link">&larr; Back to sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-auth">
      <div className="pt-auth__card">
        <div className="pt-auth__brand">
          <Brand size={28} />
        </div>
        <h1 className="pt-auth__title">
          Reset your <em>password.</em>
        </h1>
        <p className="pt-auth__lede">
          Enter the email associated with your account and we&rsquo;ll send a reset code.
        </p>

        <form onSubmit={submitRequest}>
          <div className="pt-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              className="pt-input"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          {error && (
            <p className="pt-auth__note" role="alert" style={{ color: "var(--red)" }}>{error}</p>
          )}
          <button type="submit" className="pt-btn pt-btn--primary pt-auth__submit" disabled={busy}>
            {busy ? "Sending…" : "Send reset code →"}
          </button>
        </form>

        <p className="pt-auth__alt">
          <Link to="/sign-in" className="pt-auth__link">&larr; Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (COGNITO_ENABLED) return <CognitoResetPassword />;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    navigate("/check-email");
  };

  return (
    <div className="pt-auth">
      <div className="pt-auth__card">
        <div className="pt-auth__brand">
          <Brand size={28} />
        </div>
        <h1 className="pt-auth__title">
          Reset your <em>password.</em>
        </h1>
        <p className="pt-auth__lede">
          Enter the email associated with your account and we&rsquo;ll send a reset link.
        </p>

        <form onSubmit={onSubmit}>
          <div className="pt-field">
            <label htmlFor="email">Email</label>
            <input id="email" className="pt-input" type="email" autoComplete="email" placeholder="you@example.com" required />
          </div>
          <button type="submit" className="pt-btn pt-btn--primary pt-auth__submit" disabled={busy}>
            {busy ? "Sending…" : "Send reset link →"}
          </button>
        </form>

        <p className="pt-auth__alt">
          <Link to="/sign-in" className="pt-auth__link">&larr; Back to sign in</Link>
        </p>
        <p className="pt-auth__note">
          Dev environment — password reset is simulated (no email is actually sent).
        </p>
      </div>
    </div>
  );
}
